// src\components\account\input\TextInput.js
'use client';

import { useRef, useState } from 'react';

import { cn } from '@/core/lib/utils';

// src\components\account\input\TextInput.js

// src\components\account\input\TextInput.js

// src\components\account\input\TextInput.js

// src\components\account\input\TextInput.js

// src\components\account\input\TextInput.js

// src\components\account\input\TextInput.js

export default function TextInput({
  label,
  name,
  register,
  errors,
  placeholder,
  disabled = false,
  type = 'text',
  inputClassName = '',
  className = '',
  labelClassName = '',
  variant = 'default',
  required = false,
  allowDecimal = false,
  textSize = 'text-sm',
}) {
  const [isFocused, setIsFocused] = useState(false);
  const [displayValue, setDisplayValue] = useState('');
  const inputRef = useRef(null);

  const getNestedError = (name, errors) => {
    return name.split('.').reduce((acc, key) => acc?.[key], errors);
  };

  const error = getNestedError(name, errors);

  const formatNumber = value => {
    if (!value) return '';

    if (allowDecimal) {
      // Keep digits and one decimal point only
      const parts = value.split('.');
      const integerPart = parts[0].replace(/\D/g, '');
      // Limit decimal places to 2 digits
      const decimalPart =
        parts.length > 1 ? '.' + parts[1].replace(/\D/g, '').slice(0, 2) : '';
      const cleanValue = integerPart + decimalPart;

      if (!cleanValue || cleanValue === '.') return '';

      // Format integer part with commas, but keep decimal part
      const numParts = cleanValue.split('.');
      const formattedInteger = new Intl.NumberFormat('en-US').format(
        Number.parseInt(numParts[0] || '0', 10)
      );
      return numParts.length > 1
        ? formattedInteger + '.' + numParts[1]
        : formattedInteger;
    }

    // Integer only - strip everything except digits
    const digits = value.replace(/\D/g, '');
    if (!digits) return '';
    return new Intl.NumberFormat('en-US').format(Number.parseInt(digits, 10));
  };

  const handleNumberInput = e => {
    if (type === 'number') {
      const input = e.target;
      const rawValue = input.value;
      const cursorPosition = input.selectionStart;

      let digits;

      if (allowDecimal) {
        // Allow digits and one decimal point
        digits = rawValue.replace(/[^\d.]/g, '');
        // Ensure only one decimal point
        const parts = digits.split('.');
        if (parts.length > 2) {
          digits = parts[0] + '.' + parts.slice(1).join('');
        }
        // Limit decimal places to 2 digits
        if (parts.length === 2 && parts[1].length > 2) {
          digits = parts[0] + '.' + parts[1].slice(0, 2);
        }
      } else {
        // Integer only
        digits = rawValue.replace(/[^\d]/g, '');
      }

      // Format the number
      const formatted = formatNumber(digits);

      // Update display value for overlay
      setDisplayValue(formatted);

      // Calculate new cursor position after formatting
      const beforeCursor = rawValue.slice(0, cursorPosition);
      let beforeCursorDigits;

      if (allowDecimal) {
        beforeCursorDigits = beforeCursor.replace(/[^\d.]/g, '');
        const beforeParts = beforeCursorDigits.split('.');
        if (beforeParts.length > 2) {
          beforeCursorDigits =
            beforeParts[0] + '.' + beforeParts.slice(1).join('');
        }
        // Limit decimal places for cursor calculation too
        if (beforeParts.length === 2 && beforeParts[1].length > 2) {
          beforeCursorDigits =
            beforeParts[0] + '.' + beforeParts[1].slice(0, 2);
        }
      } else {
        beforeCursorDigits = beforeCursor.replace(/[^\d]/g, '');
      }

      const formattedBeforeCursor = formatNumber(beforeCursorDigits);
      const newCursorPosition = formattedBeforeCursor.length;

      // Update input value
      input.value = formatted;

      // Restore cursor position
      setTimeout(() => {
        input.setSelectionRange(newCursorPosition, newCursorPosition);
      }, 0);
    }
  };

  const registration = register ? register(name) : {};

  const combinedOnChange = e => {
    if (type === 'number') {
      handleNumberInput(e);

      // Create a new event with the unformatted value for react-hook-form
      const unformattedValue = e.target.value.replace(/,/g, '');

      // Create proper synthetic event for react-hook-form
      Object.defineProperty(e, 'target', {
        writable: true,
        value: {
          ...e.target,
          value: unformattedValue,
          name: name,
        },
      });

      registration.onChange?.(e);
    } else {
      // For text inputs, directly call the registration onChange
      registration.onChange?.(e);
    }
  };

  const handleRef = el => {
    inputRef.current = el;
    if (registration.ref) {
      registration.ref(el);
    }
    if (el && type === 'number') {
      setTimeout(() => {
        if (el.value) {
          const formatted = formatNumber(el.value);
          el.value = formatted;
          setDisplayValue(formatted);
        }
      }, 100);
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col justify-start',
        variant === 'default' ? 'gap-2' : '',
        className
      )}
    >
      {label && (
        <label
          className={labelClassName || 'text-primary-600 font-bold'}
          htmlFor={name}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <input
          {...registration}
          ref={handleRef}
          onChange={combinedOnChange}
          id={name}
          name={name}
          type="text"
          placeholder={placeholder}
          disabled={disabled}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          inputMode={
            type === 'number' ? (allowDecimal ? 'decimal' : 'numeric') : 'text'
          }
          className={cn(
            textSize,
            'h-10 border-primary-300 placeholder-primary-500 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-0 focus:border-primary-700 w-full',
            error ? 'border-red-600' : '',
            variant === 'default' ? 'border rounded-md py-2 px-3' : 'border-b',
            type === 'number' && displayValue && !isFocused
              ? 'text-transparent caret-transparent'
              : '',
            type === 'number' ? 'text-end [direction:ltr]' : '',
            inputClassName
          )}
        />

        {type === 'number' && displayValue && !isFocused && (
          <div
            className={cn(
              textSize,
              'absolute inset-0 pointer-events-none flex items-center text-primary-900',
              inputClassName.includes('text-center')
                ? 'justify-center'
                : 'justify-start',
              variant === 'default' ? 'px-3' : ''
            )}
          >
            {displayValue}
          </div>
        )}
      </div>

      {error && <p className="text-red-600">{error.message}</p>}
    </div>
  );
}
